import React, { useState, useEffect, useCallback, useContext } from "react";
import { CircularProgress } from "@mui/material";
import styles from "./insight.module.css";
import OutlinedCard from "Samarth Module/components/recommendationCard/card";
import Insight from "./insight";
import InsightMenu from "./insightMenu";
import CustomButton from "../../../components/buttons/CustomButton";
import { SelectedAlertsContext } from "utils/SelectedAlertContext";
import { ALERT_CONFIG } from "./../../../constants/alertInsightConfig";

const AlertInsight = ({
  alertDetails,
  onCancel,
  setDrawerState,
  alertType,
  recommendationKey = "resolveData",
  statusKey = "Alert status",
  isReadOnly,
}) => {
  const [insightDetails, setInsightDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRecom, setSelectedRecom] = useState([]);
  const { selections, setSelections } = useContext(SelectedAlertsContext);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [menuSections, setMenuSections] = useState([]);

  const config = ALERT_CONFIG[alertType];

  const recommendations = ((alertDetails['RecommendationOld'] === 'Other Recommendation' && alertDetails['Alert status'] === 'Rejected')
  ? [{...alertDetails[recommendationKey][0],Title: "Other Recommendation", Info: "", isRecommended: "" }]
  : (alertDetails[recommendationKey] ?? [])).slice().sort((a, b) => {
    const isTopPriority = (item) =>
      item?.Title?.includes("IUT") &&
      item?.["Supplying Site Cluster"] === alertDetails["Site cluster"];
 
    if (isTopPriority(a) && !isTopPriority(b)) return -1;
    if (!isTopPriority(a) && isTopPriority(b)) return 1;
 
    if (a.Title.includes(alertDetails["Recommendation"]) && !b.Title.includes(alertDetails["Recommendation"])) return -1;
    if (!a.Title.includes(alertDetails["Recommendation"]) && b.Title.includes(alertDetails["Recommendation"])) return 1;
    return 0;
  });

  const alertTypeKey = alertType === 'RMPM Alert' ? 'rmpm' :
    alertType === 'Contract Alert' ? 'contract' : 'agileOr';

  // Initialize menu sections and default selected item
  useEffect(() => {
    const sections = config.menuSections.map(section => ({
      ...section,
      isOpen: true
    }));
    setMenuSections(sections);
    setSelectedMenuItem(sections[0]?.items[0]);
  }, [config]);

  const handleNavigate = (item) => {
    item = 'OEE Insights' === item ? 'OEE Trend' : item;
    setSelectedMenuItem(item);
    const sectionId = item.replace(/\s+/g, '-').toLowerCase();
    const rightPanel = document.querySelector(`.${styles.insightRightPanel}`);

    if (rightPanel) {
      const escapedSectionId = sectionId.replace(/'/g, "\\'");
      const sectionElement = rightPanel.querySelector(`#${escapedSectionId}`);

      if (sectionElement) {
        const panelRect = rightPanel.getBoundingClientRect();
        const elementRect = sectionElement.getBoundingClientRect();
        const scrollPosition = elementRect.top - panelRect.top + rightPanel.scrollTop - 10;

        rightPanel.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const toggleSection = (sectionIndex) => {
    setMenuSections(prev => {
      const updated = [...prev];
      updated[sectionIndex].isOpen = !updated[sectionIndex].isOpen;
      return updated;
    });
  };

  const fetchAlertData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [demandData, insightData] = await Promise.all([
        config.apiHandlers.fetchDemandData(alertDetails),
        config.apiHandlers?.fetchInsightData(alertDetails),
      ]);

      const primaryInsight = {
        question: config.menuSections[0].items[0],
        data: demandData,
      };
      const realtimeInsight = {
        question: "Real time update",
        data:[]
      }

      const updatedInsightDetails = [primaryInsight, ...insightData, realtimeInsight];
      setInsightDetails(updatedInsightDetails);
    } catch (err) {
      console.error("Error fetching alert details:", err);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [alertDetails, config]);

  // Find matching alert in selections
  useEffect(() => {
    const alertId = alertDetails["Alert ID"];
    const acceptedAlerts = selections[alertTypeKey]?.accepted || [];
    const matchingAlert = acceptedAlerts.find(entry =>
      entry["Alert ID"] === alertId
    );
    setSelectedRecom(matchingAlert?.selectedRecom || []);
  }, [selections, alertDetails, alertTypeKey]);

  useEffect(() => {
    fetchAlertData();
  }, [fetchAlertData]);

  const addToCart = () => {
    const alertId = alertDetails["Alert ID"];

    setSelections(prevSelections => {
      const updatedSelections = { ...prevSelections };

      if (!updatedSelections[alertTypeKey]) {
        updatedSelections[alertTypeKey] = { accepted: [], rejected: [] };
      }

      // Remove the alert from both accepted and rejected lists first
      updatedSelections[alertTypeKey].accepted = updatedSelections[alertTypeKey].accepted.filter(
        entry => entry["Alert ID"] !== alertId
      );
      updatedSelections[alertTypeKey].rejected = updatedSelections[alertTypeKey].rejected.filter(
        entry => entry["Alert ID"] !== alertId
      );

      // Add to accepted list if there are selected recommendations
      if (selectedRecom.length > 0) {
        updatedSelections[alertTypeKey].accepted.push({
          ...alertDetails,
          selectedRecom
        });
      }

      return updatedSelections;
    });

    setDrawerState(false);
  };

  const headerDetails = config.getHeaderDetails(alertDetails);

  return (
    <div className={styles.insightContainer}>
      <div className={styles.insightDetails}>
        <div className={styles.insightHeaderDivider}>
          <span>Insights for {alertDetails["Alert ID"]}</span>
          <div className={styles.insightHeaderDetails}>
            {Object.entries(headerDetails).map(([key, value]) => (
              <span key={key}>{key}: {value}</span>
            ))}
          </div>
        </div>
        <div className={styles.insightBody}>
          <div className={styles.insightLeftPanel}>
            <InsightMenu
              sections={menuSections}
              onNavigate={handleNavigate}
              selectedItem={selectedMenuItem}
              onToggleSection={toggleSection}
            />
          </div>
          <div className={styles.insightRightPanel}>
            {loading ? (
              <div className={styles.loaderContainer}>
                <CircularProgress />
              </div>
            ) : error ? (
              <div className={styles.errorContainer}>{error}</div>
            ) : (
              <>
                {insightDetails && (
                  <div className={styles.otherInsight}>
                    <Insight
                      data={insightDetails}
                      alertDetails={alertDetails}
                      alertType={alertType}
                      materialID={alertDetails["Material Id"]}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div className={styles.recommandationDetails}>
        <div className={styles.recommandationHeader}>
          Recommendation ({recommendations.length})
        </div>
        <div className={styles.recommandationBody}>
          <div className={styles.recommandationCards}>
            {recommendations.length > 0 ? (
              recommendations.map((alert, index) => (
                <OutlinedCard
                  key={index}
                  alert={alert}
                  alertDetails={alertDetails}
                  alertType={alertType}
                  recommandationCard={alertDetails["Recommendation"]}
                  selectedRecom={selectedRecom}
                  isReadOnly={isReadOnly}
                  setSelectedRecom={setSelectedRecom}
                />
              ))
            ) : (
              <div>No recommendations available</div>
            )}
          </div>
          <div className={styles.recommandationFooter}>
            <CustomButton label="Cancel" type="cancel" onClick={() => onCancel(false)} />
            <CustomButton
              label="Add to cart"
              type="addToCart"
              disabled={!selectedRecom.length || alertDetails[statusKey] === 'Closed' || alertDetails[statusKey] === 'Accepted' || alertDetails[statusKey] === 'Rejected'}
              onClick={addToCart}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertInsight;